import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { SubCategory } from '../types/sub-category.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_SUB_CATEGORIES_API_PATHS } from '../constants/sub-categories-api-paths.constants';

export interface GetSubCategoriesParams {
  categoryId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: {
    isActive?: boolean;
    category?: string;
    archived?: boolean;
  };
}

type UseAdminGetSubCategoriesResult = {
  isLoading: boolean;
  isRefetching: boolean;
  subCategories: SubCategory[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetSubCategoriesResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  subCategories: [],
  totalCount: 0,
};

export function useAdminGetSubCategories(params?: GetSubCategoriesParams): UseAdminGetSubCategoriesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const limit = params?.limit ?? 1000;
  const sortBy = params?.sortBy ?? 'createdAt';
  const sortDirection = params?.sortDirection ?? 'asc';
  const page = params?.page;
  const serializedFilters = JSON.stringify({
    isActive: params?.filters?.isActive,
    category: params?.filters?.category ?? params?.categoryId,
    archived: params?.filters?.archived,
  });

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; subCategories: SubCategory[] }>(
        ADMIN_SUB_CATEGORIES_API_PATHS.GET_SUB_CATEGORIES,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        subCategories: response.subCategories,
        totalCount: response.totalCount,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des sous-rubriques'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, subCategories: [], totalCount: 0 });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
